import { useMutation } from "@tanstack/react-query"
import { queryClient } from "@/lib/queryClient"
import { attachmentQueue } from "@/lib/powersync/attachments"
import { queuePhotoThumbnail } from "@/lib/powersync/photoThumbnails"
import {
  releasePhotoBlob,
  stashPhotoBlob,
} from "@/lib/powersync/inMemoryPhotoCache"
import { getCachedUserId } from "@/lib/auth/cachedSession"
import { PhotoInput } from "@/lib/queryClient/PartialSuccessError"

/**
 * Photo record returned from the database.
 */
export type PhotoRecord = {
  id: string
  filename: string
  original_filename: string | null
  file_path: string
  file_size_bytes: number | null
  mime_type: string | null
  width_px: number | null
  height_px: number | null
  caption: string | null
  created_by: string
  created_at: string
  updated_at: string
}

/**
 * Entity types that can have photos attached.
 */
export type PhotoEntityType = "survey_site" | "observation" | "geospatial_data"

/**
 * Subject folder used in a photo's storage path, per entity type. Also what
 * tells the two photo buckets apart — see
 * `@fieldbook/common/lib/photoBuckets`.
 */
const SUBJECT_FOLDERS: Record<PhotoEntityType, string> = {
  survey_site: "survey_sites",
  observation: "observations",
  geospatial_data: "geospatial_data",
}

/**
 * Payload for uploading a photo.
 * Contains all data needed for the mutation and for optimistic updates.
 *
 * Note: `file` is optional because after rehydration, the file is retrieved
 * from IndexedDB using the photo ID.
 */
export type UploadPhotoPayload = {
  id: string
  entity_id: string
  entity_type: PhotoEntityType
  org_id: string
  caption?: string
  // File metadata stored for rehydration
  filename: string
  file_size: number
  mime_type: string
  // The actual file - only present on initial mutation, not after rehydration
  file?: File | Blob
  /**
   * Stable creation timestamp set at staging time so retries do not
   * change the photo's sort position. Falls back to
   * `new Date().toISOString()` if absent (e.g. legacy callers).
   */
  created_at?: string
}

/**
 * Pending photo for optimistic UI updates.
 */
export type PendingPhoto = {
  id: string
  entity_id: string
  entity_type: string
  caption?: string
  url: string
}

/**
 * Mutation key for photo upload - used for registry lookup.
 * All photo uploads share this key; individual items are identified
 * by inspecting mutation.state.variables.id
 */
export const UPLOAD_PHOTO_MUTATION_KEY = ["photos", "upload"]

/**
 * Derive a deterministic v5-shaped UUID from a (entityId, photoId) pair.
 * Used as the association row id so retries through the attachment queue
 * replace the same row instead of creating duplicate associations.
 */
export async function deterministicAssociationId(
  entityId: string,
  photoId: string,
): Promise<string> {
  const data = new TextEncoder().encode(`${entityId}:${photoId}`)
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", data))
  const bytes = digest.slice(0, 16)
  // Stamp version (5) and RFC 4122 variant bits for a valid UUID shape.
  bytes[6] = (bytes[6] & 0x0f) | 0x50
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}

type PhotoTx = {
  execute: (query: string, parameters?: unknown[]) => Promise<unknown>
}

async function selectProjectId(
  tx: PhotoTx,
  table: string,
  entityId: string,
): Promise<string | undefined> {
  const result = await tx.execute(
    `SELECT project_id FROM ${table} WHERE id = ?`,
    [entityId],
  )
  const rows = (
    result as { rows?: { _array?: Array<{ project_id?: string }> } }
  ).rows?._array
  return rows?.[0]?.project_id
}

/**
 * Association tables for geospatial photos.
 *
 * A record the user hasn't uploaded yet lives in `local_geospatial_data`, and
 * its photo associations have to stay local with it — the synced table's row
 * would reference a `geospatial_data` id the server has never seen.
 * {@link uploadGeospatialRecord} moves both across together.
 */
export const GEOSPATIAL_PHOTO_TABLES = {
  synced: "geospatial_data_photos",
  local: "local_geospatial_data_photos",
} as const

export type GeospatialPhotoTable =
  (typeof GEOSPATIAL_PHOTO_TABLES)[keyof typeof GEOSPATIAL_PHOTO_TABLES]

/**
 * Locate a geospatial record across the synced and local-only tables, and say
 * which association table its photos belong in.
 */
export async function resolveGeospatialPhotoTarget(
  tx: PhotoTx,
  recordId: string,
): Promise<{ projectId: string; table: GeospatialPhotoTable }> {
  const localProjectId = await selectProjectId(
    tx,
    "local_geospatial_data",
    recordId,
  )
  if (localProjectId) {
    return { projectId: localProjectId, table: GEOSPATIAL_PHOTO_TABLES.local }
  }

  const syncedProjectId = await selectProjectId(tx, "geospatial_data", recordId)
  if (syncedProjectId) {
    return { projectId: syncedProjectId, table: GEOSPATIAL_PHOTO_TABLES.synced }
  }

  throw new Error(
    `Cannot upload photo: geospatial_data ${recordId} has no project_id`,
  )
}

async function getProjectIdForPhotoEntity(
  tx: PhotoTx,
  entityType: PhotoEntityType,
  entityId: string,
): Promise<string> {
  if (entityType === "geospatial_data") {
    const { projectId } = await resolveGeospatialPhotoTarget(tx, entityId)
    return projectId
  }

  const table = entityType === "survey_site" ? "survey_sites" : "observations"
  const projectId = await selectProjectId(tx, table, entityId)

  if (!projectId) {
    throw new Error(
      `Cannot upload photo: ${entityType} ${entityId} has no project_id`,
    )
  }

  return projectId
}

/**
 * Cancel and remove any pending upload mutations for a specific photo.
 * Also cleans up the IndexedDB entry.
 */
export const cancelUploadPhoto = async (photoId: string): Promise<void> => {
  const mutations = queryClient.getMutationCache().findAll({
    mutationKey: UPLOAD_PHOTO_MUTATION_KEY,
    predicate: ({ state }) => {
      const variables = state.variables as UploadPhotoPayload | undefined
      return variables?.id === photoId
    },
  })

  for (const mutation of mutations) {
    queryClient.getMutationCache().remove(mutation)
  }
}

/**
 * Get the file path for a photo in storage.
 */
const getFilePath = (
  orgId: string,
  entityType: PhotoEntityType,
  entityId: string,
  photoId: string,
  filename: string,
): string => {
  const fileExt = filename.split(".").pop()
  if (!fileExt) throw new Error(`No file extension available for ${filename}`)
  return `${orgId}/${SUBJECT_FOLDERS[entityType]}/${entityId}/${photoId}.${fileExt}`
}

/**
 * Update upload progress in the query cache.
 */
export const updateUploadProgress = (photoId: string, percentage: number) => {
  const queryKey = ["photoUploads", photoId]
  if (percentage !== 100) {
    queryClient.setQueryData<number>(queryKey, percentage)
  } else {
    queryClient.removeQueries({ queryKey })
  }
}

/**
 * Wraps the mutation function for uploading a photo for use in the default
 * mutation options (ie, when the mutation is rehydrated).
 *
 * Both survey_site and observation photos now use the attachment queue.
 * After rehydration, the attachment queue handles file upload automatically —
 * DB records are already in PowerSync local SQLite.
 */
export const uploadPhotoFn = async (
  payload: UploadPhotoPayload,
): Promise<PhotoRecord> => {
  if (!payload.file) {
    // After rehydration, the attachment queue handles file upload.
    // DB records are already in PowerSync local SQLite.
    throw new Error(
      `Photo ${payload.id} cannot be re-uploaded after rehydration — attachment queue handles this`,
    )
  }

  return innerUploadPhotoFn(payload)
}

/**
 * Upload a survey_site photo via PowerSync + AttachmentQueue.
 *
 * On entry:
 *   - Stashes the blob in the in-memory cache so consumers can render
 *     thumbnails immediately, before the queue has persisted bytes to IDB.
 * Then hands off to attachmentQueue.saveFile() which:
 *   1. Writes the file blob to local storage (instant, works offline).
 *   2. Inserts the photo and association rows via its updateHook.
 *   3. Queues the file for upload to Supabase Storage in the background.
 *
 * The blob is released from the in-memory cache once saveFile completes
 * (success or failure), since by then either the bytes are in IDB or the
 * upload won't be recovered from memory anyway.
 */
export async function uploadSurveySitePhoto(
  payload: UploadPhotoPayload & { file: File | Blob },
): Promise<PhotoRecord> {
  const {
    id: photoId,
    entity_id,
    org_id,
    caption,
    filename,
    file_size,
    mime_type,
    file,
  } = payload

  stashPhotoBlob(photoId, file)

  try {
    const filePath = getFilePath(
      org_id,
      "survey_site",
      entity_id,
      photoId,
      filename,
    )
    // Keep the timestamp stable across retries.
    const now = payload.created_at ?? new Date().toISOString()
    const fileExt = filename.split(".").pop() ?? "jpg"

    const userId = await getCachedUserId()
    if (!userId) {
      throw new Error("Cannot upload photo: user is not authenticated")
    }

    const fileData = await file.arrayBuffer()

    await attachmentQueue.saveFile({
      id: photoId,
      data: fileData,
      fileExtension: fileExt,
      mediaType: mime_type,
      updateHook: async (tx) => {
        await tx.execute(
          `INSERT OR REPLACE INTO photos (id, filename, original_filename, file_path, file_size_bytes, mime_type, caption, created_by, organisation_id, width_px, height_px, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            photoId,
            filename,
            filename,
            filePath,
            file_size,
            mime_type,
            caption ?? null,
            userId,
            org_id,
            null,
            null,
            now,
            now,
          ],
        )
        const projectId = await getProjectIdForPhotoEntity(
          tx,
          "survey_site",
          entity_id,
        )
        const associationId = await deterministicAssociationId(
          entity_id,
          photoId,
        )
        await tx.execute(
          `INSERT OR REPLACE INTO survey_site_photos (id, survey_site_id, photo_id, project_id, created_at)
           VALUES (?, ?, ?, ?, ?)`,
          [associationId, entity_id, photoId, projectId, now],
        )
      },
    })
    void queuePhotoThumbnail(photoId, file)

    return {
      id: photoId,
      filename,
      original_filename: filename,
      file_path: filePath,
      file_size_bytes: file_size,
      mime_type,
      caption: caption ?? null,
      created_by: userId,
      width_px: null,
      height_px: null,
      created_at: now,
      updated_at: now,
    }
  } finally {
    releasePhotoBlob(photoId)
  }
}

/**
 * Upload an observation photo via PowerSync + AttachmentQueue.
 *
 * See uploadSurveySitePhoto for the full pattern.
 */
export async function uploadObservationPhoto(
  payload: UploadPhotoPayload & { file: File | Blob },
): Promise<PhotoRecord> {
  const {
    id: photoId,
    entity_id,
    org_id,
    caption,
    filename,
    file_size,
    mime_type,
    file,
  } = payload

  stashPhotoBlob(photoId, file)

  try {
    const filePath = getFilePath(
      org_id,
      "observation",
      entity_id,
      photoId,
      filename,
    )
    // Single canonical timestamp per photo — see uploadSurveySitePhoto.
    const now = payload.created_at ?? new Date().toISOString()
    const fileExt = filename.split(".").pop() ?? "jpg"

    const userId = await getCachedUserId()
    if (!userId) {
      throw new Error("Cannot upload photo: user is not authenticated")
    }

    const fileData = await file.arrayBuffer()

    await attachmentQueue.saveFile({
      id: photoId,
      data: fileData,
      fileExtension: fileExt,
      mediaType: mime_type,
      updateHook: async (tx) => {
        await tx.execute(
          `INSERT OR REPLACE INTO photos (id, filename, original_filename, file_path, file_size_bytes, mime_type, caption, created_by, organisation_id, width_px, height_px, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            photoId,
            filename,
            filename,
            filePath,
            file_size,
            mime_type,
            caption ?? null,
            userId,
            org_id,
            null,
            null,
            now,
            now,
          ],
        )
        const projectId = await getProjectIdForPhotoEntity(
          tx,
          "observation",
          entity_id,
        )
        const associationId = await deterministicAssociationId(
          entity_id,
          photoId,
        )
        await tx.execute(
          `INSERT OR REPLACE INTO observation_photos (id, observation_id, photo_id, project_id, created_at)
           VALUES (?, ?, ?, ?, ?)`,
          [associationId, entity_id, photoId, projectId, now],
        )
      },
    })
    void queuePhotoThumbnail(photoId, file)

    return {
      id: photoId,
      filename,
      original_filename: filename,
      file_path: filePath,
      file_size_bytes: file_size,
      mime_type,
      caption: caption ?? null,
      created_by: userId,
      width_px: null,
      height_px: null,
      created_at: now,
      updated_at: now,
    }
  } finally {
    releasePhotoBlob(photoId)
  }
}

/**
 * Upload a geospatial record photo via PowerSync + AttachmentQueue.
 *
 * See uploadSurveySitePhoto for the full pattern. The one difference is that
 * the association table is resolved per record rather than fixed: a record the
 * user hasn't uploaded yet keeps its associations in the local-only mirror,
 * because the server has no `geospatial_data` row for them to point at. The
 * `photos` row and the file itself go up straight away either way — the photo
 * is the thing that can't be re-taken.
 */
export async function uploadGeospatialDataPhoto(
  payload: UploadPhotoPayload & { file: File | Blob },
): Promise<PhotoRecord> {
  const {
    id: photoId,
    entity_id,
    org_id,
    caption,
    filename,
    file_size,
    mime_type,
    file,
  } = payload

  stashPhotoBlob(photoId, file)

  try {
    const filePath = getFilePath(
      org_id,
      "geospatial_data",
      entity_id,
      photoId,
      filename,
    )
    // Single canonical timestamp per photo — see uploadSurveySitePhoto.
    const now = payload.created_at ?? new Date().toISOString()
    const fileExt = filename.split(".").pop() ?? "jpg"

    const userId = await getCachedUserId()
    if (!userId) {
      throw new Error("Cannot upload photo: user is not authenticated")
    }

    const fileData = await file.arrayBuffer()

    await attachmentQueue.saveFile({
      id: photoId,
      data: fileData,
      fileExtension: fileExt,
      mediaType: mime_type,
      updateHook: async (tx) => {
        await tx.execute(
          `INSERT OR REPLACE INTO photos (id, filename, original_filename, file_path, file_size_bytes, mime_type, caption, created_by, organisation_id, width_px, height_px, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            photoId,
            filename,
            filename,
            filePath,
            file_size,
            mime_type,
            caption ?? null,
            userId,
            org_id,
            null,
            null,
            now,
            now,
          ],
        )
        // Resolve when bytes land: the record may have been uploaded while
        // the file was being written.
        const { projectId, table } = await resolveGeospatialPhotoTarget(
          tx,
          entity_id,
        )
        const associationId = await deterministicAssociationId(
          entity_id,
          photoId,
        )
        await tx.execute(
          `INSERT OR REPLACE INTO ${table} (id, geospatial_data_id, photo_id, project_id, created_at)
           VALUES (?, ?, ?, ?, ?)`,
          [associationId, entity_id, photoId, projectId, now],
        )
      },
    })
    void queuePhotoThumbnail(photoId, file)

    return {
      id: photoId,
      filename,
      original_filename: filename,
      file_path: filePath,
      file_size_bytes: file_size,
      mime_type,
      caption: caption ?? null,
      created_by: userId,
      width_px: null,
      height_px: null,
      created_at: now,
      updated_at: now,
    }
  } finally {
    releasePhotoBlob(photoId)
  }
}

/**
 * Dispatches to the appropriate photo upload function based on entity type.
 */
export async function innerUploadPhotoFn(
  payload: UploadPhotoPayload,
): Promise<PhotoRecord> {
  const file = payload.file
  if (!file) {
    throw new Error(`File not attached for ${payload.id}`)
  }

  if (payload.entity_type === "survey_site") {
    return uploadSurveySitePhoto({ ...payload, file })
  }

  if (payload.entity_type === "geospatial_data") {
    return uploadGeospatialDataPhoto({ ...payload, file })
  }

  return uploadObservationPhoto({ ...payload, file })
}

/**
 * Persist a batch of photo blobs one at a time.
 *
 * AttachmentQueue serializes remote uploads, but saving multiple large source
 * files concurrently still creates overlapping ArrayBuffers and IndexedDB
 * writes in the page process. Keeping this stage sequential bounds memory use
 * on Mobile Safari without delaying the parent entity's local-first save.
 *
 * A failure on one photo does not stop the rest — the others are still worth
 * saving — so failures are collected and returned rather than thrown. Callers
 * holding the only remaining copy of the bytes (a form draft) must check the
 * result before dropping it: `onError` alone cannot be awaited, and an empty
 * return is the only signal that every photo reached durable storage.
 */
export async function persistPhotoInputsSequentially({
  photos,
  entityId,
  entityType,
  onError,
}: {
  photos: PhotoInput[]
  entityId: string
  entityType: PhotoEntityType
  onError?: (photo: PhotoInput, error: unknown) => void
}): Promise<PhotoInput[]> {
  const failed: PhotoInput[] = []
  for (const photo of photos) {
    if (!photo.file) continue
    try {
      await innerUploadPhotoFn({
        ...photo,
        entity_id: entityId,
        entity_type: entityType,
        file: photo.file,
      })
    } catch (error) {
      failed.push(photo)
      onError?.(photo, error)
    }
  }
  return failed
}

/**
 * Success handler for photo upload.
 * Exported so it can be registered in mutation defaults for rehydration.
 */
export const onSuccessUploadPhoto = async (
  data: PhotoRecord,
  payload: UploadPhotoPayload,
) => {
  const { id: photoId, entity_id, entity_type } = payload

  // Update the photos cache with the real data
  queryClient.setQueriesData<PhotoRecord[]>(
    { queryKey: ["photos", entity_type, entity_id] },
    (oldData) => {
      if (!oldData) return [data]
      // Replace pending entry with real data
      const filtered = oldData.filter((p) => p.id !== photoId)
      return [...filtered, data]
    },
  )

  // Invalidate to ensure fresh data
  await queryClient.invalidateQueries({
    queryKey: ["photos", entity_type, entity_id],
  })

  // Clean up upload progress
  queryClient.removeQueries({ queryKey: ["photoUploads", photoId] })
}

/**
 * Hook for uploading a photo.
 * Supports offline persistence and rehydration.
 *
 * @param entityId - The ID of the entity (observation or survey_site) to attach the photo to.
 *   Used to set the mutation scope so uploads are sequenced with other entity mutations.
 *
 * Usage:
 * ```
 * const { mutate } = useUploadPhoto(observationId)
 * mutate({
 *   id: photoId,
 *   entity_id: observationId,
 *   entity_type: "observation",
 *   org_id: org.id,
 *   filename: file.name,
 *   file_size: file.size,
 *   mime_type: file.type,
 *   file: file,
 *   caption: "Optional caption",
 * })
 * ```
 */
export const useUploadPhoto = (entityId: string) => {
  return useMutation({
    mutationKey: UPLOAD_PHOTO_MUTATION_KEY,
    mutationFn: innerUploadPhotoFn,
    networkMode: "always",
    scope: { id: `entity-${entityId}` },
    onMutate: async (payload: UploadPhotoPayload) => {
      const { id: photoId, entity_id, entity_type, org_id, filename } = payload

      const filePath = getFilePath(
        org_id,
        entity_type,
        entity_id,
        photoId,
        filename,
      )

      // Create optimistic pending photo
      const pendingItem: PendingPhoto = {
        id: photoId,
        entity_id,
        entity_type,
        caption: payload.caption,
        url: filePath,
      }

      // Snapshot previous data for rollback
      const previousPhotos = queryClient.getQueryData<
        (PhotoRecord | PendingPhoto)[]
      >(["photos", entity_type, entity_id])

      // Add pending item to cache
      queryClient.setQueriesData<(PhotoRecord | PendingPhoto)[]>(
        { queryKey: ["photos", entity_type, entity_id] },
        (oldData) => [...(oldData || []), pendingItem],
      )

      return { previousPhotos }
    },
    onSuccess: onSuccessUploadPhoto,
  })
}

/**
 * Hook to get the upload progress for a photo.
 * Returns the percentage (0-100) or undefined if not uploading.
 */
export const usePhotoUploadProgress = (
  photoId?: string,
): number | undefined => {
  return queryClient.getQueryData<number>(["photoUploads", photoId ?? ""])
}
